import { Fragment } from "react";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { translateNormalizedRole } from "@utils/roles";
import { useTranslation } from "react-i18next";
import type { MovementServiceStatusDetails } from "../types";

interface DetailsMovementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  statusDetails?: MovementServiceStatusDetails[];
}

const serviceVesselCall = "bff-vessel-call";
const LANG_BASE = "vessel-call.movement-services.preview.details";
const TEST_ID_BASE = "movement-service-details";

const DetailsMovementDrawer = ({ isOpen, onClose, statusDetails }: DetailsMovementDrawerProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const hasStatusDetailsContent = (item: MovementServiceStatusDetails) =>
    Boolean(
      item.rejectionReason ||
        item.internalNotes ||
        item.externalNotes ||
        item.comments ||
        (Array.isArray(item.rejectionReasonAttachments) &&
          item.rejectionReasonAttachments.length > 0) ||
        (Array.isArray(item.internalAttachments) && item.internalAttachments.length > 0) ||
        (Array.isArray(item.externalAttachments) && item.externalAttachments.length > 0) ||
        (Array.isArray(item.berthPlan) && item.berthPlan.length > 0),
    );

  const getSubmittedByName = (item: MovementServiceStatusDetails) =>
    item.submittedByFullName?.trim() ||
    item.submittedByPreferredUsername?.trim() ||
    translateNormalizedRole(t, item.submittedBy);

  const getSubmittedByRole = (item: MovementServiceStatusDetails) =>
    translateNormalizedRole(t, item.submittedByRole);

  const getCommentsFieldKey = (item: MovementServiceStatusDetails) =>
    item.status?.includes("CANCELLED") ? "cancel-reason" : "comments";

  const getCommentsLabel = (item: MovementServiceStatusDetails) =>
    item.status?.includes("CANCELLED")
      ? t(`${LANG_BASE}.cancel-reason`)
      : t("vessel-call.movement-services.comments");

  return (
    <div data-testid="details-movement-service" className="max-w-sm">
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onClose}
        title={t(`${LANG_BASE}.title`)}
        submitLabel={t("common.ok")}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          {statusDetails?.map((item, idx) => (
            <Fragment key={`status-detail-${idx}`}>
              {item.rejectionReason && (
                <div className="col-span-full flex flex-col gap-2" key={`rejection-reason-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.rejection-reason`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-rejection-reason`}
                  >
                    {item.rejectionReason}
                  </div>
                </div>
              )}
              {Array.isArray(item.rejectionReasonAttachments) &&
                item.rejectionReasonAttachments[0] && (
                  <AttachmentsComponent
                    key={`rejection-reason-attachment-${idx}`}
                    data-testid={`${TEST_ID_BASE}-rejection-reason-attachment`}
                    documentName={item.rejectionReasonAttachments[0]?.originalFileName || "-"}
                    onDownload={() => {
                      handleDownload(
                        "document",
                        serviceVesselCall,
                        item.rejectionReasonAttachments && item.rejectionReasonAttachments[0]?.url
                          ? item.rejectionReasonAttachments[0].url
                          : "-",
                      );
                    }}
                    onPreview={() =>
                      handlePreview(
                        "document",
                        serviceVesselCall,
                        item.rejectionReasonAttachments && item.rejectionReasonAttachments[0]?.url
                          ? item.rejectionReasonAttachments[0].url
                          : "-",
                      )
                    }
                    langBase={LANG_BASE}
                    documentType={t(`${LANG_BASE}.rejection-attachment`)}
                  />
                )}
              {item.externalNotes && (
                <div className="col-span-full flex flex-col gap-2" key={`notes-to-agent-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.notes-to-agent`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-notes-to-agent`}
                  >
                    {item.externalNotes}
                  </div>
                </div>
              )}
              {item.comments && (
                <div
                  className="col-span-full flex flex-col gap-2"
                  key={`${getCommentsFieldKey(item)}-${idx}`}
                >
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {getCommentsLabel(item)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-${getCommentsFieldKey(item)}`}
                  >
                    {item.comments}
                  </div>
                </div>
              )}
              {Array.isArray(item.externalAttachments) && item.externalAttachments[0] && (
                <AttachmentsComponent
                  key={`attachments-${idx}`}
                  data-testid={`${TEST_ID_BASE}-attachments`}
                  documentName={
                    (item.externalAttachments && item.externalAttachments[0]?.originalFileName) ||
                    "-"
                  }
                  onDownload={() => {
                    handleDownload(
                      "document",
                      serviceVesselCall,
                      item.externalAttachments && item.externalAttachments[0]?.url
                        ? item.externalAttachments[0].url
                        : "-",
                    );
                  }}
                  onPreview={() =>
                    handlePreview(
                      "document",
                      serviceVesselCall,
                      item.externalAttachments && item.externalAttachments[0]?.url
                        ? item.externalAttachments[0].url
                        : "-",
                    )
                  }
                  langBase={LANG_BASE}
                  documentType={t(`${LANG_BASE}.attachments`)}
                />
              )}
              {item.internalNotes && (
                <div className="col-span-full flex flex-col gap-2" key={`internal-notes-${idx}`}>
                  <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                    {t(`${LANG_BASE}.internal-notes`)}
                  </CustomFormLabel>
                  <div
                    className="text-sm whitespace-pre-wrap wrap-break-word"
                    data-testid={`${TEST_ID_BASE}-internal-notes`}
                  >
                    {item.internalNotes}
                  </div>
                </div>
              )}
              {Array.isArray(item.internalAttachments) && item.internalAttachments[0] && (
                <AttachmentsComponent
                  key={`internal-attachment-${idx}`}
                  data-testid={`${TEST_ID_BASE}-internal-attachment`}
                  documentName={
                    (item.internalAttachments && item.internalAttachments[0]?.originalFileName) ||
                    "-"
                  }
                  onDownload={() => {
                    handleDownload(
                      "document",
                      serviceVesselCall,
                      item.internalAttachments && item.internalAttachments[0]?.url
                        ? item.internalAttachments[0].url
                        : "-",
                    );
                  }}
                  onPreview={() =>
                    handlePreview(
                      "document",
                      serviceVesselCall,
                      item.internalAttachments && item.internalAttachments[0]?.url
                        ? item.internalAttachments[0].url
                        : "-",
                    )
                  }
                  langBase={LANG_BASE}
                  documentType={t(`${LANG_BASE}.internal-attachment`)}
                />
              )}
              {Array.isArray(item.berthPlan) && item.berthPlan[0] && (
                <AttachmentsComponent
                  key={`berth-plan-attachment-${idx}`}
                  data-testid={`${TEST_ID_BASE}-berth-plan-attachment`}
                  documentName={
                    (item.berthPlan &&
                      Array.isArray(item.berthPlan) &&
                      item.berthPlan[0]?.originalFileName) ||
                    "-"
                  }
                  onDownload={() => {
                    handleDownload(
                      "document",
                      serviceVesselCall,
                      item.berthPlan && Array.isArray(item.berthPlan) && item.berthPlan[0]?.url
                        ? item.berthPlan[0].url
                        : "-",
                    );
                  }}
                  onPreview={() =>
                    handlePreview(
                      "document",
                      serviceVesselCall,
                      item.berthPlan && Array.isArray(item.berthPlan) && item.berthPlan[0]?.url
                        ? item.berthPlan[0].url
                        : "-",
                    )
                  }
                  langBase={LANG_BASE}
                  documentType={t(`${LANG_BASE}.berth-plan`)}
                />
              )}
              {hasStatusDetailsContent(item) && (
                <div>
                  <p className="text-xs text-gray-500">
                    <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
                    {getSubmittedByName(item) || getSubmittedByRole(item) ? (
                      <>
                        {getSubmittedByName(item) && (
                          <span className="text-gray-800">{getSubmittedByName(item)}</span>
                        )}
                        {getSubmittedByName(item) && getSubmittedByRole(item) ? " / " : null}
                        {getSubmittedByRole(item)}
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {item.timestamp ? formatDateTime(item.timestamp) : "-"}
                  </p>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </SideDrawer>
    </div>
  );
};
export default DetailsMovementDrawer;
